import { Formik } from 'formik';
import { Props, Values } from './PaymentForm.types';
import * as Yup from 'yup';
import FormField from '../../components/field/FormField';
import Button from '../../components/button/Button';
import { useTranslation } from '../../context/translationContext';
import { useCurrentCompany } from '../../hooks/useCompany';
import { Loader } from '../../components/loader/Loader';
import { useParams } from 'react-router-dom';
import { DirectionDoc } from '../../types/Invoice';
import { Currency } from '../../types/Currency';
import { useEffect, useState } from 'react';
import http from '../../api/axios';
import { dateSubmitFormat } from '../../types/core/date.types';
import { apiUrl } from '../../api/apiUrl';
import { format } from 'date-fns';
import { PaymentMethod, PaymentMethodTranslated } from '../../types/Payment';
import { ObjectFromEnum, convertArrayToObjArray } from '../../utils/functions/data.functions';
import SelectAutocomplete from '../../components/select/SelectAutocomplete';
import { CompanyBankAccount } from '../../types/CompanyBankAccount';

/**
 *  Form component for creating / editing payments for document
 */
export const PaymentForm = ({ document, onSubmit, initialValues, onBack }: Props) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { company } = useCurrentCompany();
  const [currencies, setCurrencies] = useState<Currency[] | null>(null);
  const [companyBankAccounts, setCompanyBankAccounts] = useState<CompanyBankAccount[] | []>([]);

  const _initialValues: Values = {
    accountingDocumentId: id ?? '',
    currency: document?.currency ?? '',
    destination: '',
    direction: DirectionDoc.INPUT_DOC,
    price: 0,
    source: '',
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    paymentSpecifications: '',
    paymentDate: format(new Date(), dateSubmitFormat),
  };

  /**
   * Loads payment by date
   */
  const fetchCurrenciesData = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.CURRENCY_GET_LIST}?date=${format(new Date(), dateSubmitFormat)}`,
    });
    if (response?.data) setCurrencies(response?.data);
  };

  /**
   * Loads bank account list for current company
   */
  const fetchCompanyBankAccountData = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.COMPANY_BANK_ACCOUNT_LIST}?parentId=${company.id}`,
    });
    setCompanyBankAccounts(response?.data);
  };

  /**
   * Fill input when account number from autocomplete is selected or create new, when none is found
   * new type string, existing is of type object
   */
  const handleChangeAccNumber = (
    val: string | CompanyBankAccount,
    field: string,
    setFieldValue: (name: string, val: any) => void,
  ) => {
    if (typeof val === 'object') {
      setFieldValue(field, val?.number);
      // setPayment({ ...payment, bankAccountNumber: val.number, swift: swift, iban: iban });
    } else {
      // new item
      setFieldValue(field, val);
    }
  };

  const PaymentSchema = Yup.object().shape({
    direction: Yup.string().required('Direction is required.'),
    price: Yup.string().required('Price is required.'),
    currency: Yup.string().required('Currency is required.'),
    paymentMethod: Yup.string().required('Payment method is required.'),
    paymentDate: Yup.string().required('Payment date is required.'),
  });

  useEffect(() => {
    fetchCurrenciesData();
    fetchCompanyBankAccountData();
  }, []);

  if (!company || !currencies) return <Loader />;
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues || _initialValues}
      onSubmit={onSubmit}
      validationSchema={PaymentSchema}
    >
      {({ errors, touched, values, setFieldValue }) => (
        <div className="w-1/2 text-center">
          {values.direction === DirectionDoc.INPUT_DOC && (
            <>
              <div className="my-4">
                <FormField
                  id="source"
                  name="source"
                  type="text"
                  label={t('page.payment.input-source')}
                  errors={errors}
                  touched={touched}
                />
              </div>
              <div className="my-4">
                <SelectAutocomplete
                  autofocus={false}
                  data={
                    values.paymentMethod == PaymentMethod.BANK_TRANSFER ? companyBankAccounts : []
                  }
                  value={values.destination}
                  name="destination"
                  labelKey="name"
                  valueKey="number"
                  label={t('page.payment.input-destination')}
                  placeholder={t('page.payment.input-destination')}
                  className={`border border-gray-600 text-gray-900 text-sm rounded-lg focus:border-gray-500 block w-full p-2.5 focus:border-gray-600 min-w-full`}
                  isReadOnly={false}
                  onSelected={(val) => handleChangeAccNumber(val, 'destination', setFieldValue)}
                  onChange={(val) => handleChangeAccNumber(val, 'destination', setFieldValue)}
                  displayValue={'number'}
                  resize={false}
                  searchWhileEmpty
                />
              </div>
            </>
          )}
          {values.direction === DirectionDoc.OUTPUT_DOC && (
            <>
              <div className="my-4">
                <SelectAutocomplete
                  data={
                    values.paymentMethod == PaymentMethod.BANK_TRANSFER ? companyBankAccounts : []
                  }
                  value={values.source}
                  name="source"
                  labelKey="name"
                  valueKey="number"
                  label={t('page.payment.input-source')}
                  placeholder={'Source'}
                  className={`border border-gray-600 text-gray-900 text-sm rounded-lg focus:border-gray-500 block w-full p-2.5 focus:border-gray-600 min-w-full`}
                  isReadOnly={false}
                  onSelected={(val) => handleChangeAccNumber(val, 'source', setFieldValue)}
                  onChange={(val) => handleChangeAccNumber(val, 'source', setFieldValue)}
                  displayValue={'number'}
                  resize={false}
                  searchWhileEmpty
                />
              </div>
              <div className="my-4">
                <FormField
                  id="destination"
                  name="destination"
                  type="text"
                  label={t('page.payment.input-destination')}
                  errors={errors}
                  touched={touched}
                />
              </div>
            </>
          )}
          <div className="my-4">
            <FormField
              id="price"
              name="price"
              type="number"
              label={t('page.payment.input-price')}
              errors={errors}
              touched={touched}
            />
          </div>
          <div className="my-4">
            <FormField
              type="select"
              id="currency"
              name="currency"
              label={t('page.payment.input-currency')}
              showEmptyOption
              errors={errors}
              touched={touched}
            >
              {currencies.map((opt: Currency, index: number) => (
                <option key={index} value={opt.code}>
                  {opt.country + ' - ' + opt.code}
                </option>
              ))}
            </FormField>
          </div>
          <div className="my-4">
            <FormField
              id="paymentDate"
              name="paymentDate"
              type="date"
              label={t('page.payment.input-payment-date')}
              errors={errors}
              touched={touched}
            />
          </div>
          <div className="my-4">
            <FormField
              type="select"
              id="paymentMethod"
              name="paymentMethod"
              label={t('page.payment.input-payment-method')}
              errors={errors}
              touched={touched}
            >
              {convertArrayToObjArray(Object.values(PaymentMethod), PaymentMethodTranslated, t).map(
                (opt: ObjectFromEnum, index: number) => (
                  <option key={index} value={opt.value}>
                    {opt.name}
                  </option>
                ),
              )}
            </FormField>
          </div>
          {values.paymentMethod === PaymentMethod.OTHER && (
            <div className="my-4">
              <FormField
                id="paymentSpecifications"
                name="paymentSpecifications"
                type="text"
                label={t('page.payment.input-payment-specifications')}
                errors={errors}
                touched={touched}
              />
            </div>
          )}
          <div className="flex float-left">
            <Button title={t('general.back')} color="gray" className="mr-3" onClick={onBack} />
            <Button
              title={t('general.save')}
              color="green"
              type="button"
              onClick={async () => {
                await PaymentSchema.validate(
                  {
                    ...values,
                  },
                  { abortEarly: false },
                ),
                  onSubmit(values);
              }}
              showLoading
            />
          </div>
        </div>
      )}
    </Formik>
  );
};
